import type { ActionFunctionArgs } from '@remix-run/node';
import { json } from '@remix-run/node';
import { z } from 'zod';
import { updateMessageReaction } from '~/modules/chat/api/update-message-reaction.server';

export const REACT_MESSAGE_API = '/remix-api/chat/react-message';

const FORM_DATA_SCHEMA = z.object({
    messageID: z.coerce.number().int().positive(),
    reaction: z.string().trim().min(1, 'Reaction emoji is required'),
    isRemove: z.boolean().nullish(),
});

/**
 * API endpoint to add or remove an emoji reaction on a chat message.
 * Use with the `useConversation` hook.
 * @see {@link packages/web/app/modules/chat/hooks/use-conversation.ts}
 */
export const action = async ({ request, context }: ActionFunctionArgs) => {
    const { logger } = context;
    const payload = await request.json();
    const result = FORM_DATA_SCHEMA.safeParse(payload);

    if (!result.success) {
        return json(null, { status: 400 });
    }

    try {
        const response = await updateMessageReaction({
            context,
            messageID: result.data.messageID,
            reaction: result.data.reaction,
            isRemove: result.data.isRemove ?? undefined,
        });

        if (!response) {
            return json(null, { status: 500 });
        }

        let statusCode = 200;
        if (
            response.__typename === 'NotAuthorizedError' ||
            response.__typename === 'NotFoundError'
        ) {
            statusCode = 403;
        } else if (response.__typename === 'UserError') {
            statusCode = 400;
        } else if (response.__typename === 'ServerError') {
            statusCode = 500;
        }

        return json({ response }, { status: statusCode });
    } catch (error) {
        logger.error(`Error in ${REACT_MESSAGE_API}`, { error });
        return json(null, { status: 500 });
    }
};
